const INITIAL_STATE = [
  0x6a09e667, 0xbb67ae85, 0x3c6ef372, 0xa54ff53a, 0x510e527f, 0x9b05688c, 0x1f83d9ab, 0x5be0cd19,
] as const;

const ROUND_CONSTANTS = new Uint32Array([
  0x428a2f98, 0x71374491, 0xb5c0fbcf, 0xe9b5dba5, 0x3956c25b, 0x59f111f1, 0x923f82a4, 0xab1c5ed5,
  0xd807aa98, 0x12835b01, 0x243185be, 0x550c7dc3, 0x72be5d74, 0x80deb1fe, 0x9bdc06a7, 0xc19bf174,
  0xe49b69c1, 0xefbe4786, 0x0fc19dc6, 0x240ca1cc, 0x2de92c6f, 0x4a7484aa, 0x5cb0a9dc, 0x76f988da,
  0x983e5152, 0xa831c66d, 0xb00327c8, 0xbf597fc7, 0xc6e00bf3, 0xd5a79147, 0x06ca6351, 0x14292967,
  0x27b70a85, 0x2e1b2138, 0x4d2c6dfc, 0x53380d13, 0x650a7354, 0x766a0abb, 0x81c2c92e, 0x92722c85,
  0xa2bfe8a1, 0xa81a664b, 0xc24b8b70, 0xc76c51a3, 0xd192e819, 0xd6990624, 0xf40e3585, 0x106aa070,
  0x19a4c116, 0x1e376c08, 0x2748774c, 0x34b0bcb5, 0x391c0cb3, 0x4ed8aa4a, 0x5b9cca4f, 0x682e6ff3,
  0x748f82ee, 0x78a5636f, 0x84c87814, 0x8cc70208, 0x90befffa, 0xa4506ceb, 0xbef9a3f7, 0xc67178f2,
]);

/** Incremental SHA-256 for bounded browser uploads; it retains at most one 64-byte block. */
export class IncrementalSha256 {
  readonly #state = new Uint32Array(INITIAL_STATE);
  readonly #buffer = new Uint8Array(64);
  readonly #schedule = new Uint32Array(64);
  #bufferLength = 0;
  #bytesHashed = 0;
  #finished = false;

  update(input: Uint8Array): this {
    if (this.#finished) throw new Error("SHA-256 digest is already finalized.");
    if (!Number.isSafeInteger(input.byteLength) || this.#bytesHashed + input.byteLength > 2 ** 40) {
      throw new Error("SHA-256 input is too large.");
    }
    this.#bytesHashed += input.byteLength;
    let offset = 0;
    if (this.#bufferLength > 0) {
      const take = Math.min(64 - this.#bufferLength, input.byteLength);
      this.#buffer.set(input.subarray(0, take), this.#bufferLength);
      this.#bufferLength += take;
      offset += take;
      if (this.#bufferLength === 64) {
        this.#process(this.#buffer);
        this.#bufferLength = 0;
      }
    }
    while (offset + 64 <= input.byteLength) {
      this.#process(input.subarray(offset, offset + 64));
      offset += 64;
    }
    if (offset < input.byteLength) {
      const remaining = input.subarray(offset);
      this.#buffer.set(remaining, 0);
      this.#bufferLength = remaining.byteLength;
    }
    return this;
  }

  digest(): Uint8Array {
    if (this.#finished) throw new Error("SHA-256 digest is already finalized.");
    this.#finished = true;
    const bitLength = BigInt(this.#bytesHashed) * 8n;
    this.#buffer[this.#bufferLength] = 0x80;
    this.#bufferLength += 1;
    if (this.#bufferLength > 56) {
      this.#buffer.fill(0, this.#bufferLength);
      this.#process(this.#buffer);
      this.#bufferLength = 0;
    }
    this.#buffer.fill(0, this.#bufferLength, 56);
    for (let index = 0; index < 8; index += 1) {
      this.#buffer[63 - index] = Number((bitLength >> BigInt(index * 8)) & 0xffn);
    }
    this.#process(this.#buffer);

    const output = new Uint8Array(32);
    const view = new DataView(output.buffer);
    for (let index = 0; index < this.#state.length; index += 1) {
      view.setUint32(index * 4, this.#state[index] ?? 0, false);
    }
    return output;
  }

  digestHex(): `sha256:${string}` {
    return `sha256:${Array.from(this.digest(), (byte) => byte.toString(16).padStart(2, "0")).join("")}`;
  }

  #process(block: Uint8Array): void {
    const schedule = this.#schedule;
    for (let index = 0; index < 16; index += 1) {
      const offset = index * 4;
      schedule[index] =
        (((block[offset] ?? 0) << 24) |
          ((block[offset + 1] ?? 0) << 16) |
          ((block[offset + 2] ?? 0) << 8) |
          (block[offset + 3] ?? 0)) >>>
        0;
    }
    for (let index = 16; index < 64; index += 1) {
      schedule[index] =
        (smallSigma1(schedule[index - 2] ?? 0) +
          (schedule[index - 7] ?? 0) +
          smallSigma0(schedule[index - 15] ?? 0) +
          (schedule[index - 16] ?? 0)) >>>
        0;
    }

    let a = this.#state[0] ?? 0;
    let b = this.#state[1] ?? 0;
    let c = this.#state[2] ?? 0;
    let d = this.#state[3] ?? 0;
    let e = this.#state[4] ?? 0;
    let f = this.#state[5] ?? 0;
    let g = this.#state[6] ?? 0;
    let h = this.#state[7] ?? 0;
    for (let index = 0; index < 64; index += 1) {
      const first =
        (h +
          bigSigma1(e) +
          ((e & f) ^ (~e & g)) +
          (ROUND_CONSTANTS[index] ?? 0) +
          (schedule[index] ?? 0)) >>>
        0;
      const second = (bigSigma0(a) + ((a & b) ^ (a & c) ^ (b & c))) >>> 0;
      h = g;
      g = f;
      f = e;
      e = (d + first) >>> 0;
      d = c;
      c = b;
      b = a;
      a = (first + second) >>> 0;
    }
    this.#state[0] = ((this.#state[0] ?? 0) + a) >>> 0;
    this.#state[1] = ((this.#state[1] ?? 0) + b) >>> 0;
    this.#state[2] = ((this.#state[2] ?? 0) + c) >>> 0;
    this.#state[3] = ((this.#state[3] ?? 0) + d) >>> 0;
    this.#state[4] = ((this.#state[4] ?? 0) + e) >>> 0;
    this.#state[5] = ((this.#state[5] ?? 0) + f) >>> 0;
    this.#state[6] = ((this.#state[6] ?? 0) + g) >>> 0;
    this.#state[7] = ((this.#state[7] ?? 0) + h) >>> 0;
  }
}

function rotateRight(value: number, shift: number): number {
  return (value >>> shift) | (value << (32 - shift));
}

function smallSigma0(value: number): number {
  return rotateRight(value, 7) ^ rotateRight(value, 18) ^ (value >>> 3);
}

function smallSigma1(value: number): number {
  return rotateRight(value, 17) ^ rotateRight(value, 19) ^ (value >>> 10);
}

function bigSigma0(value: number): number {
  return rotateRight(value, 2) ^ rotateRight(value, 13) ^ rotateRight(value, 22);
}

function bigSigma1(value: number): number {
  return rotateRight(value, 6) ^ rotateRight(value, 11) ^ rotateRight(value, 25);
}
